/**
 * Save orchestration for the plan editor (spec 2026-08-22-02 §4 / plan step 3).
 *
 * The diff is replayed in dependency order — create assets, then create their
 * representations (atomically registered into the frame, spec 2026-08-22-01
 * §10), then patch changed representations, then delete the ones whose geometry
 * is gone. Deleting geometry never deletes an asset: the asset is reported back
 * so the page can say "N assets are no longer drawn".
 *
 * A failing call does not abort the run — every element is self-contained, so a
 * partial save leaves a consistent scene and the page can retry just what
 * failed.
 */
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../../api'
import { useApi } from '../../api/hooks'
import { queryKeys } from '../../hooks/useQueries'
import { registrationTransformFor, type FrameCalibration } from './engine/calibrate'
import type { Transform } from './engine/geometry'
import {
    NETWORK_KIND_PROPERTY,
    ROOM_NUMBER_PROPERTY,
    UNIT_KIND_PROPERTY,
    resolvePendingRunLinks,
    type PlanDiff,
    type RegistrationEdit,
} from './engine/scene'

export const PLAN_SCENE_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/scene'

export interface SaveOpFailure {
    kind: 'asset' | 'asset_update' | 'create' | 'update' | 'delete' | 'registration' | 'reparent' | 'calibration' | 'relation'
    label: string
    message: string
    /** Set for update / delete failures so the baseline can keep them pending. */
    repUid?: string
    /**
     * The API's machine-readable error code, when it sent one. The page keys
     * its *localized, inline* messages off this: a raw backend `title` is
     * English and belongs in the summary, not on a field (spec 2026-08-30-01,
     * `## Verification`).
     */
    code?: string
    /**
     * The pending asset this failure is about — set on `asset` failures, so a
     * grouper the combobox created can be pointed at on the field the user
     * typed it into.
     */
    assetKey?: string
    /**
     * The *space* a `relation` failure is about, as its uid or its pending key
     * — the element whose Grouping field has to show the problem.
     */
    spaceKey?: string
}

export interface SaveOutcome {
    /** Pending-asset key → the uid the API assigned. */
    createdAssets: Record<string, string>
    /** Representation-group id → the representation uid the API assigned. */
    createdReps: Record<string, { repUid: string; registrationUid?: string }>
    failures: SaveOpFailure[]
    orphanedAssets: { uid: string; name: string }[]
    /** Registrations written successfully — the page drops their pending edit. */
    writtenRegistrations: string[]
    /** True once the calibration PATCH landed, so the page clears its edit. */
    calibrationWritten: boolean
    counts: {
        assets: number
        reps: number
        updates: number
        deletes: number
        registrations: number
        reparents: number
        /** Groupers whose kind or code changed (spec 2026-08-30-03 §4). */
        assetUpdates: number
        /** `group_member` edges written or removed (spec 2026-08-30-01 §9). */
        relations: number
    }
}

export interface SaveProgress {
    total: number
    done: number
}

/**
 * The API's error code, when the body carries one. `{code, title}` is what
 * every `WriteError` answers; the cardinality violation uses the lowercase
 * `relation_cardinality_violated`.
 */
const codeOf = (err: unknown): string | undefined => {
    if (err && typeof err === 'object') {
        const e = err as { code?: string; error?: { code?: string } }
        return e.error?.code || e.code || undefined
    }
    return undefined
}

const messageOf = (err: unknown): string => {
    if (err && typeof err === 'object') {
        // `title` is where this API puts the human sentence — every
        // `WriteError` answers `{code, title}` — so a 409 like "a unit named
        // 'Lot B02' already exists in this building group" reaches the save
        // summary verbatim instead of being flattened to "unknown error".
        const e = err as { message?: string; title?: string; error?: { message?: string; title?: string } }
        return e.error?.message || e.error?.title || e.title || e.message || 'unknown error'
    }
    return String(err)
}

export function useSavePlan(org: string, frameAssetUid: string) {
    const queryClient = useQueryClient()
    const [saving, setSaving] = useState(false)
    const [progress, setProgress] = useState<SaveProgress>({ total: 0, done: 0 })

    const { request } = useApi()
    const patchRegistration = $api.useMutation(
        'patch',
        '/api/v1/organizations/{orgCode}/frame-registrations/{registrationUid}',
    )
    const createAsset = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets')
    const patchAsset = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/assets/{assetUid}')
    const createRep = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/representations')
    const updateRep = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/representations/{repUid}')
    const deleteRep = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/representations/{repUid}')
    const createRelation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/relations')
    const setProperties = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties')
    const deleteRelation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/asset-relations/{relationUid}')

    /**
     * Refreshes everything the saved geometry feeds: the scene the editor
     * reloads from, the composite render and the asset queries.
     *
     * The frame-registrations list is **not** in here any more (spec
     * 2026-08-25-04 §1): saving geometry no longer writes registration rows, so
     * invalidating that list on every save was refetching something nothing had
     * touched. The two flows that still write rows — "use as underlay" and a
     * calibration/placement PATCH — call `invalidateRegistrations` themselves.
     */
    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key[0] === 'composite-plan') return true
                if (key[0] !== 'get') return false
                const path = key[1]
                return (
                    path === PLAN_SCENE_PATH ||
                    // The riser diagram is read off the floor plans just saved.
                    path === '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/risers' ||
                    path === '/api/v1/organizations/{orgCode}/assets/{assetUid}/representations' ||
                    path === '/api/v1/organizations/{orgCode}/assets'
                )
            },
        })
        queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, frameAssetUid) })
    }, [queryClient, org, frameAssetUid])

    /** The registration rows themselves — only the flows that write one. */
    const invalidateRegistrations = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key[0] === 'composite-plan') return true
                if (key[0] !== 'get') return false
                return (
                    key[1] === '/api/v1/organizations/{orgCode}/assets/{assetUid}/frame/registrations' ||
                    key[1] === PLAN_SCENE_PATH
                )
            },
        })
    }, [queryClient])

    /**
     * Writes one registration's new placement (spec 2026-08-22-03 §1, §2, §6).
     *
     * The editor manipulates the **composed** transform the scene endpoint
     * hands out; the row stores its own hop. Reading the registration back
     * recovers the parent chain, so a plan registered into a unit that is
     * itself registered into the floor lands correctly. A registration that
     * cannot be read is assumed to target the edited frame directly — which it
     * does in every case the editor can currently produce.
     */
    const writeRegistration = useCallback(
        async (edit: RegistrationEdit): Promise<void> => {
            let own: Transform | undefined
            try {
                const current = await request(
                    '/api/v1/organizations/{orgCode}/frame-registrations/{registrationUid}',
                    'GET',
                    { params: { path: { orgCode: org, registrationUid: edit.registrationUid } } },
                )
                own = (current as { transform?: Transform } | undefined)?.transform
            } catch {
                // Fall through to the direct-hop assumption below.
            }
            const transform = own ? registrationTransformFor(edit.base, own, edit.transform) : edit.transform
            await patchRegistration.mutateAsync({
                params: { path: { orgCode: org, registrationUid: edit.registrationUid } },
                body: { transform, provenance: edit.provenance },
            } as never)
        },
        [org, request, patchRegistration],
    )

    /**
     * Writes the frame's calibration onto the primary plan's registration
     * (spec 2026-08-25-04 §3). No transform travels with it: recalibrating is
     * not a move, and the server leaves the placement untouched.
     */
    const writeCalibration = useCallback(
        async (registrationUid: string, calibration: FrameCalibration | null): Promise<void> => {
            await patchRegistration.mutateAsync({
                params: { path: { orgCode: org, registrationUid } },
                body: { calibration },
            } as never)
        },
        [org, patchRegistration],
    )

    const save = useCallback(
        async (diff: PlanDiff, assetTypeUidOf: (code: string) => string | undefined): Promise<SaveOutcome> => {
            const outcome: SaveOutcome = {
                createdAssets: {},
                createdReps: {},
                failures: [],
                orphanedAssets: diff.orphanedAssets,
                writtenRegistrations: [],
                calibrationWritten: false,
                counts: { assets: 0, reps: 0, updates: 0, deletes: 0, registrations: 0, reparents: 0, relations: 0, assetUpdates: 0 },
            }
            const total =
                diff.createAssets.length + diff.createReps.length + diff.updateReps.length +
                diff.deleteReps.length + diff.updateRegistrations.length + diff.reparentAssets.length +
                diff.createRelations.length + diff.deleteRelations.length +
                (diff.updateCalibration ? 1 : 0)
            setSaving(true)
            setProgress({ total, done: 0 })
            let done = 0
            const step = () => {
                done += 1
                setProgress({ total, done })
            }

            try {
                for (const op of diff.createAssets) {
                    const typeUid = assetTypeUidOf(op.typeCode)
                    if (!typeUid) {
                        outcome.failures.push({ kind: 'asset', label: op.name || op.typeCode, message: `unknown asset type ${op.typeCode}` })
                        step()
                        continue
                    }
                    // A pending parent (the room drawn moments ago) resolves
                    // here: the ops arrive parents-first, so its uid is in.
                    const parentUid = (op.parentKey ? outcome.createdAssets[op.parentKey] : undefined) ?? op.parentUid
                    try {
                        const created = await createAsset.mutateAsync({
                            params: { path: { orgCode: org } },
                            body: {
                                // An unnamed room sends no name at all, and the
                                // API stores NULL rather than `''` (spec
                                // 2026-08-31-02 §2). "Has no name" is the state
                                // the fallback reads; a blank one is a name.
                                ...(op.name ? { name: op.name } : {}),
                                asset_type_uid: typeUid,
                                ...(parentUid ? { parent_asset_uid: parentUid } : {}),
                                // A grouper's code and kind travel with the
                                // creation (spec 2026-08-30-03 §3): the code is
                                // an external id under the `code` source, and
                                // `unit_kind` is a property the create endpoint
                                // takes by code. One request, not three. A
                                // room's number rides the same bag.
                                ...(op.code ? { external_ids: { code: op.code } } : {}),
                                // A network asset's network rides the same bag.
                                ...(op.unitKind || op.roomNumber !== undefined || op.networkKind
                                    ? {
                                        properties: {
                                            ...(op.unitKind ? { [UNIT_KIND_PROPERTY]: op.unitKind } : {}),
                                            ...(op.roomNumber !== undefined
                                                ? { [ROOM_NUMBER_PROPERTY]: op.roomNumber } : {}),
                                            ...(op.networkKind ? { [NETWORK_KIND_PROPERTY]: op.networkKind } : {}),
                                        },
                                    }
                                    : {}),
                            },
                        } as never) as { uid?: string }
                        if (created?.uid) {
                            outcome.createdAssets[op.key] = created.uid
                            outcome.counts.assets += 1
                        }
                    } catch (err) {
                        outcome.failures.push({
                            kind: 'asset',
                            label: op.name || op.typeCode,
                            message: messageOf(err),
                            code: codeOf(err),
                            assetKey: op.key,
                        })
                    }
                    step()
                }

                for (const op of diff.createReps) {
                    const assetUid = op.assetUid ?? (op.assetKey ? outcome.createdAssets[op.assetKey] : undefined)
                    if (!assetUid) {
                        outcome.failures.push({ kind: 'create', label: op.label, message: 'owning asset was not created' })
                        step()
                        continue
                    }
                    try {
                        const created = await createRep.mutateAsync({
                            params: { path: { orgCode: org, assetUid } },
                            body: {
                                kind: 'vector',
                                // Runs drawn for a network asset this save just
                                // created point at its uid, not at its key.
                                value: resolvePendingRunLinks(op.value, outcome.createdAssets),
                                // **No `frame` in the common case** (spec
                                // 2026-08-25-04 §1). Geometry authored in its
                                // owner's own frame resolves implicitly; an
                                // identity row carried no information and made
                                // the drawing vanish when it went missing. The
                                // exception is a cross-layer footprint, where
                                // the row is what says *which layer*.
                                ...(op.registerInFrame ? { frame: { target_asset_uid: frameAssetUid } } : {}),
                            },
                        } as never) as { uid?: string }
                        if (created?.uid) {
                            // `registrationUid` stays absent on purpose: the
                            // common case writes no row (§1), and the
                            // cross-layer one is not read back — the scene
                            // refetch after a clean save carries it instead.
                            outcome.createdReps[op.groupId] = { repUid: created.uid }
                            outcome.counts.reps += 1
                        }
                    } catch (err) {
                        outcome.failures.push({ kind: 'create', label: op.label, message: messageOf(err) })
                    }
                    step()
                }

                // Membership edges (spec 2026-08-30-01 §9), after the assets
                // and their geometry so both ends of every edge exist. A
                // replacement deletes before it creates: a space is a
                // `group_member` of at most one grouper, and the two rows must
                // never coexist.
                for (const op of diff.deleteRelations) {
                    try {
                        await deleteRelation.mutateAsync({
                            params: { path: { orgCode: org, relationUid: op.relationUid } },
                        } as never)
                        outcome.counts.relations += 1
                    } catch (err) {
                        outcome.failures.push({ kind: 'relation', label: op.label, message: messageOf(err) })
                    }
                    step()
                }

                for (const op of diff.createRelations) {
                    const sourceUid = op.sourceUid ?? (op.sourceKey ? outcome.createdAssets[op.sourceKey] : undefined)
                    const targetUid = op.targetUid ?? (op.targetKey ? outcome.createdAssets[op.targetKey] : undefined)
                    const spaceKey = op.sourceUid ?? op.sourceKey
                    if (!sourceUid || !targetUid) {
                        outcome.failures.push({
                            kind: 'relation',
                            label: op.label,
                            message: sourceUid ? 'the grouper was not created' : 'the space was not created',
                            code: 'grouping_dependency_failed',
                            ...(spaceKey ? { spaceKey } : {}),
                        })
                        step()
                        continue
                    }
                    try {
                        if (op.replacesRelationUid) {
                            await deleteRelation.mutateAsync({
                                params: { path: { orgCode: org, relationUid: op.replacesRelationUid } },
                            } as never)
                        }
                        await createRelation.mutateAsync({
                            params: { path: { orgCode: org, assetUid: sourceUid } },
                            body: { relation_type: 'group_member', target_asset_uid: targetUid },
                        } as never)
                        outcome.counts.relations += 1
                    } catch (err) {
                        outcome.failures.push({
                            kind: 'relation',
                            label: op.label,
                            message: messageOf(err),
                            code: codeOf(err),
                            ...(spaceKey ? { spaceKey } : {}),
                        })
                    }
                    step()
                }

                for (const op of diff.updateReps) {
                    try {
                        await updateRep.mutateAsync({
                            params: { path: { orgCode: org, repUid: op.repUid } },
                            body: { value: resolvePendingRunLinks(op.value, outcome.createdAssets) },
                        } as never)
                        outcome.counts.updates += 1
                    } catch (err) {
                        outcome.failures.push({ kind: 'update', label: op.label, message: messageOf(err), repUid: op.repUid })
                    }
                    step()
                }

                // Registrations first: re-placing a layer never depends on the
                // geometry edits of the same save, and a failure there must not
                // cost the drawing.
                for (const op of diff.updateRegistrations) {
                    try {
                        await writeRegistration(op)
                        outcome.counts.registrations += 1
                        outcome.writtenRegistrations.push(op.registrationUid)
                    } catch (err) {
                        outcome.failures.push({ kind: 'registration', label: op.registrationUid, message: messageOf(err) })
                    }
                    step()
                }

                // Kind / code edits on groupers that already exist (spec
                // 2026-08-30-03 §4). The type and the code are columns-ish —
                // one PATCH — while `unit_kind` is a property value and needs
                // its own call; both are attempted, and a failure of either is
                // reported against the same asset.
                for (const op of diff.updateAssets) {
                    const typeUid = op.typeCode ? assetTypeUidOf(op.typeCode) : undefined
                    try {
                        if (op.typeCode && !typeUid) throw new Error(`unknown asset type ${op.typeCode}`)
                        if (typeUid || op.code !== undefined || op.name !== undefined) {
                            await patchAsset.mutateAsync({
                                params: { path: { orgCode: org, assetUid: op.assetUid } },
                                body: {
                                    ...(op.name !== undefined ? { name: op.name } : {}),
                                    ...(typeUid ? { asset_type_uid: typeUid } : {}),
                                    // An empty code clears the external id.
                                    ...(op.code !== undefined ? { external_ids: { code: op.code } } : {}),
                                },
                            } as never)
                        }
                        const props = [
                            ...(op.unitKind !== undefined
                                ? [{ property_code: UNIT_KIND_PROPERTY, value: op.unitKind }] : []),
                            ...(op.roomNumber !== undefined
                                ? [{ property_code: ROOM_NUMBER_PROPERTY, value: op.roomNumber }] : []),
                        ]
                        if (props.length) {
                            await setProperties.mutateAsync({
                                params: { path: { orgCode: org, assetUid: op.assetUid } },
                                body: { properties: props },
                            } as never)
                        }
                        outcome.counts.assetUpdates += 1
                    } catch (err) {
                        outcome.failures.push({ kind: 'asset_update', label: op.label, message: messageOf(err) })
                    }
                    step()
                }

                // Re-parenting an existing asset moves its **implicit frame**
                // with it (spec 2026-08-25-04 §6) — the inspector confirmed it
                // before it ever reached the diff.
                for (const op of diff.reparentAssets) {
                    try {
                        await patchAsset.mutateAsync({
                            params: { path: { orgCode: org, assetUid: op.assetUid } },
                            body: { parent_asset_uid: op.parentUid },
                        } as never)
                        outcome.counts.reparents += 1
                    } catch (err) {
                        outcome.failures.push({ kind: 'reparent', label: op.label, message: messageOf(err) })
                    }
                    step()
                }

                if (diff.updateCalibration) {
                    const op = diff.updateCalibration
                    try {
                        await writeCalibration(op.registrationUid, op.calibration)
                        outcome.calibrationWritten = true
                    } catch (err) {
                        outcome.failures.push({ kind: 'calibration', label: op.registrationUid, message: messageOf(err) })
                    }
                    step()
                }

                for (const op of diff.deleteReps) {
                    try {
                        await deleteRep.mutateAsync({
                            params: { path: { orgCode: org, repUid: op.repUid } },
                        } as never)
                        outcome.counts.deletes += 1
                    } catch (err) {
                        outcome.failures.push({ kind: 'delete', label: op.label, message: messageOf(err), repUid: op.repUid })
                    }
                    step()
                }
            } finally {
                setSaving(false)
                invalidate()
                if (outcome.writtenRegistrations.length || outcome.calibrationWritten) invalidateRegistrations()
            }
            return outcome
        },
        [
            org, frameAssetUid, createAsset, patchAsset, setProperties, createRep, updateRep, deleteRep,
            createRelation, deleteRelation,
            writeRegistration, writeCalibration, invalidate, invalidateRegistrations,
        ],
    )

    return { save, saving, progress, writeRegistration, writeCalibration, invalidate, invalidateRegistrations }
}
